import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, X } from 'lucide-react';
import { signInWithGoogle, signInWithEmail } from '../../lib/firebase';

export default function LoginModal({ onClose, onSwitchToRegister }) {
  const navigate = useNavigate();
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  async function handleGoogle() {
    setError(''); setLoading(true);
    try {
      await signInWithGoogle();
      close();
      navigate('/platform');
    } catch (e) {
      setError(e.message.replace('Firebase: ', '').replace(/\(auth\/.*\)/, ''));
    } finally {
      setLoading(false);
    }
  }

  async function handleEmail(e) {
    e.preventDefault();
    if (!email.includes('@')) { setError('Enter a valid email address.'); return; }
    setError(''); setLoading(true);
    try {
      await signInWithEmail(email, password);
      close();
      navigate('/platform');
    } catch (err) {
      setError(err.message.replace('Firebase: ', '').replace(/\(auth\/.*\)/, ''));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-overlay" onClick={e => { if (e.target === e.currentTarget) close(); }}>
      <div className="login-modal" role="dialog" aria-modal="true" aria-label="Sign in">
        <button className="login-modal-close" onClick={close} aria-label="Close">
          <X size={16} />
        </button>

        <h2 className="form-h">Welcome back.</h2>
        <p className="form-sub">Sign in to your Investment Intelligence Daily account.</p>

        <button className="google-btn" onClick={handleGoogle} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="or-divider" style={{ margin: '18px 0' }}>
          <div className="or-line" />
          <span className="or-text">or email</span>
          <div className="or-line" />
        </div>

        <form onSubmit={handleEmail}>
          <div className="field">
            <div className="field-label">Email address</div>
            <input
              type="email"
              className="field-input"
              placeholder="you@example.com"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <div className="field-hdr">
              <div className="field-label">Password</div>
              <a href="#" className="field-link" onClick={e => e.preventDefault()}>Forgot?</a>
            </div>
            <input
              type="password"
              className="field-input"
              placeholder="••••••••"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
              {error}
            </div>
          )}

          <button type="submit" className="btn-main" disabled={loading}>
            <LogIn size={16} />
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="form-footer">
          No account?{' '}
          <a href="#" onClick={e => { e.preventDefault(); onSwitchToRegister?.(); }}>Create one free →</a>
        </div>
        <p className="disclaim">
          Not financial advice. IID provides general market research only. All investing involves risk.
        </p>
      </div>
    </div>
  );
}
